
export enum StorePermission {
  // Dashboard permissions
  VIEW_DASHBOARD = 'view_dashboard',
  
  // Inventory permissions
  VIEW_INVENTORY = 'view_inventory',
  MANAGE_INVENTORY = 'manage_inventory',
  EDIT_INVENTORY = 'edit_inventory',
  DELETE_INVENTORY = 'delete_inventory',
  
  // Sales permissions
  VIEW_SALES = 'view_sales',
  CREATE_SALES = 'create_sales',
  EDIT_SALES = 'edit_sales',
  DELETE_SALES = 'delete_sales',
  MANAGE_SALES = 'manage_sales',
  
  // Pessoas permissions (antigo Clients)
  VIEW_PESSOAS = 'view_pessoas',
  CREATE_PESSOAS = 'create_pessoas',
  EDIT_PESSOAS = 'edit_pessoas',
  DELETE_PESSOAS = 'delete_pessoas',
  
  // Aliases para compatibilidade
  /** @deprecated Use VIEW_PESSOAS */
  VIEW_CLIENTS = 'view_pessoas',
  /** @deprecated Use CREATE_PESSOAS */
  CREATE_CLIENTS = 'create_pessoas',
  /** @deprecated Use EDIT_PESSOAS */
  EDIT_CLIENTS = 'edit_pessoas',
  /** @deprecated Use DELETE_PESSOAS */
  DELETE_CLIENTS = 'delete_pessoas',
  
  // User management permissions
  MANAGE_USERS = 'manage_users',
  MANAGE_STORE_SETTINGS = 'manage_store_settings',
  MANAGE_SETTINGS = 'manage_settings',
  VIEW_ANALYTICS = 'view_analytics',
  
  // Task permissions
  VIEW_TASKS = 'view_tasks',
  CREATE_TASKS = 'create_tasks',
  EDIT_TASKS = 'edit_tasks',
  DELETE_TASKS = 'delete_tasks',
  MANAGE_TASKS = 'manage_tasks',
  MANAGE_ALL_TASKS = 'manage_all_tasks',
  
  // Team permissions
  VIEW_TEAM = 'view_team',
  MANAGE_TEAM = 'manage_team',
  MANAGE_SALES_TEAM = 'manage_sales_team',
  
  // Goals permissions
  MANAGE_GOALS = 'manage_goals',
  VIEW_GOALS = 'view_goals',
  CREATE_GOALS = 'create_goals',
  UPDATE_GOALS = 'update_goals',
  DELETE_GOALS = 'delete_goals',
  
  // Tools permissions
  VIEW_TOOLS = 'view_tools',
  
  // Supplier permissions
  VIEW_SUPPLIERS = 'view_suppliers',
  CREATE_SUPPLIERS = 'create_suppliers',
  EDIT_SUPPLIERS = 'edit_suppliers',
  DELETE_SUPPLIERS = 'delete_suppliers',
  
  // Cash permissions
  CASH_CREATE_ENTRY = 'cash_create_entry',
  CASH_CLOSE = 'cash_close',
  CASH_RECONCILE = 'cash_reconcile',
  CASH_VIEW = 'cash_view',
  
  // Preparation permissions
  PREP_CHECKLIST_UPDATE = 'prep_checklist_update',
  PREP_APPROVE_STEP = 'prep_approve_step',
  PREP_VIEW = 'prep_view',
  
  // Documentation permissions
  DOCS_UPDATE_STATUS = 'docs_update_status',
  DOCS_FLAG_DEADLINE = 'docs_flag_deadline',
  DOCS_VIEW = 'docs_view',
  
  // Contract permissions
  CONTRACTS_ISSUE = 'contracts_issue',
  CONTRACTS_FINALIZE = 'contracts_finalize',
  CONTRACTS_VIEW = 'contracts_view',
  
  // Report permissions
  REPORTS_VIEW = 'reports_view',
  REPORTS_EXPORT = 'reports_export',
  
  // Financial permissions
  FINANCIAL_CREATE = 'financial_create',
  FINANCIAL_UPDATE = 'financial_update',
  FINANCIAL_DELETE = 'financial_delete',
  FINANCIAL_VIEW = 'financial_view',
}

export enum StoreRole {
  OWNER = 'owner',
  MANAGER = 'manager',
  SALESPERSON = 'salesperson',
  VIEWER = 'viewer'
}

// StoreUserRole is deprecated - use NetworkUserRole from networkUser.ts instead
// This enum is kept for backward compatibility only
export enum StoreUserRole {
  STORE_OWNER = 'STORE_OWNER',
  STORE_MANAGER = 'STORE_MANAGER',
  STORE_SELLER = 'STORE_SELLER',
  STORE_PREPARER = 'STORE_PREPARER',
  STORE_VIEWER = 'STORE_VIEWER'
}

export interface UserAddress {
  cep?: string;
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
}

export interface StoreUser {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  nickname?: string;
  email: string;
  phone?: string;
  cpf?: string;
  gender?: 'masculino' | 'feminino' | 'nao_informado';
  address?: UserAddress;
  role: StoreUserRole;
  permissions: StorePermission[];
  isActive: boolean;
  createdAt: string;
  avatar?: string;
  isMaster?: boolean;
  username?: string;
  sales?: number;
  customPermissions?: StorePermission[];
  isCustomPermissions?: boolean;
  networkRole?: string;
  isPlatformAdmin?: boolean;
}

export interface Store {
  id: string;
  name: string;
  cnpj?: string;
  address?: string;
  phone?: string;
  email?: string;
  isActive: boolean;
  createdAt: string;
  ownerId: string;
}

export type AuthStatus = 'booting' | 'authenticated' | 'unauthenticated' | 'error';

export interface StoreAuthContextType {
  user: StoreUser | null;
  store: Store | null;
  isLoading: boolean;
  status: AuthStatus;
  login: (email: string, password: string, captchaToken?: string) => Promise<void>;
  logout: () => void;
  hasPermission: (permission: StorePermission) => boolean;
  hasAnyPermission: (permissions: StorePermission[]) => boolean;
  retry: () => Promise<void>;
}

// Default permissions by role
export const DEFAULT_STORE_PERMISSIONS: Record<StoreUserRole, StorePermission[]> = {
  [StoreUserRole.STORE_OWNER]: [
    StorePermission.VIEW_DASHBOARD,
    StorePermission.VIEW_INVENTORY,
    StorePermission.MANAGE_INVENTORY,
    StorePermission.EDIT_INVENTORY,
    StorePermission.DELETE_INVENTORY,
    StorePermission.VIEW_SALES,
    StorePermission.CREATE_SALES,
    StorePermission.EDIT_SALES,
    StorePermission.DELETE_SALES,
    StorePermission.MANAGE_SALES,
    StorePermission.VIEW_CLIENTS,
    StorePermission.CREATE_CLIENTS,
    StorePermission.EDIT_CLIENTS,
    StorePermission.DELETE_CLIENTS,
    StorePermission.MANAGE_USERS,
    StorePermission.MANAGE_STORE_SETTINGS,
    StorePermission.MANAGE_SETTINGS,
    StorePermission.VIEW_ANALYTICS,
    StorePermission.VIEW_TASKS,
    StorePermission.CREATE_TASKS,
    StorePermission.EDIT_TASKS,
    StorePermission.DELETE_TASKS,
    StorePermission.MANAGE_TASKS,
    StorePermission.MANAGE_ALL_TASKS,
    StorePermission.VIEW_TEAM,
    StorePermission.MANAGE_TEAM,
    StorePermission.MANAGE_SALES_TEAM,
    StorePermission.MANAGE_GOALS,
    StorePermission.VIEW_GOALS,
    StorePermission.CREATE_GOALS,
    StorePermission.UPDATE_GOALS,
    StorePermission.DELETE_GOALS,
    StorePermission.VIEW_TOOLS,
    StorePermission.VIEW_SUPPLIERS,
    StorePermission.CREATE_SUPPLIERS,
    StorePermission.EDIT_SUPPLIERS,
    StorePermission.DELETE_SUPPLIERS,
    StorePermission.CASH_CREATE_ENTRY,
    StorePermission.CASH_CLOSE,
    StorePermission.CASH_RECONCILE,
    StorePermission.CASH_VIEW,
    StorePermission.PREP_CHECKLIST_UPDATE,
    StorePermission.PREP_APPROVE_STEP,
    StorePermission.PREP_VIEW,
    StorePermission.DOCS_UPDATE_STATUS,
    StorePermission.DOCS_FLAG_DEADLINE,
    StorePermission.DOCS_VIEW,
    StorePermission.CONTRACTS_ISSUE,
    StorePermission.CONTRACTS_FINALIZE,
    StorePermission.CONTRACTS_VIEW,
    StorePermission.REPORTS_VIEW,
    StorePermission.REPORTS_EXPORT,
    StorePermission.FINANCIAL_CREATE,
    StorePermission.FINANCIAL_UPDATE,
    StorePermission.FINANCIAL_DELETE,
    StorePermission.FINANCIAL_VIEW,
  ],
  [StoreUserRole.STORE_MANAGER]: [
    StorePermission.VIEW_DASHBOARD,
    StorePermission.VIEW_INVENTORY,
    StorePermission.MANAGE_INVENTORY,
    StorePermission.EDIT_INVENTORY,
    StorePermission.VIEW_SALES,
    StorePermission.CREATE_SALES,
    StorePermission.EDIT_SALES,
    StorePermission.MANAGE_SALES,
    StorePermission.VIEW_CLIENTS,
    StorePermission.CREATE_CLIENTS,
    StorePermission.EDIT_CLIENTS,
    StorePermission.VIEW_ANALYTICS,
    StorePermission.VIEW_TASKS,
    StorePermission.CREATE_TASKS,
    StorePermission.EDIT_TASKS,
    StorePermission.MANAGE_TASKS,
    StorePermission.VIEW_TEAM,
    StorePermission.MANAGE_SALES_TEAM,
    StorePermission.MANAGE_GOALS,
    StorePermission.VIEW_GOALS,
    StorePermission.CREATE_GOALS,
    StorePermission.UPDATE_GOALS,
    StorePermission.VIEW_TOOLS,
    StorePermission.VIEW_SUPPLIERS,
    StorePermission.CREATE_SUPPLIERS,
    StorePermission.EDIT_SUPPLIERS,
    StorePermission.CASH_VIEW,
    StorePermission.PREP_CHECKLIST_UPDATE,
    StorePermission.PREP_APPROVE_STEP,
    StorePermission.PREP_VIEW,
    StorePermission.DOCS_UPDATE_STATUS,
    StorePermission.DOCS_VIEW,
    StorePermission.CONTRACTS_ISSUE,
    StorePermission.CONTRACTS_VIEW,
    StorePermission.REPORTS_VIEW,
    StorePermission.REPORTS_EXPORT,
    StorePermission.FINANCIAL_VIEW,
  ],
  [StoreUserRole.STORE_SELLER]: [
    StorePermission.VIEW_DASHBOARD,
    StorePermission.VIEW_INVENTORY,
    StorePermission.VIEW_SALES,
    StorePermission.CREATE_SALES,
    StorePermission.VIEW_CLIENTS,
    StorePermission.CREATE_CLIENTS,
    StorePermission.VIEW_TASKS,
    StorePermission.CREATE_TASKS,
    StorePermission.VIEW_TOOLS,
    StorePermission.VIEW_SUPPLIERS,
    StorePermission.CASH_VIEW,
    StorePermission.CONTRACTS_VIEW,
    StorePermission.REPORTS_VIEW,
    StorePermission.VIEW_GOALS,
  ],
  [StoreUserRole.STORE_PREPARER]: [
    StorePermission.VIEW_DASHBOARD,
    StorePermission.VIEW_INVENTORY,
    StorePermission.VIEW_TASKS,
    StorePermission.CREATE_TASKS,
    StorePermission.PREP_CHECKLIST_UPDATE,
    StorePermission.PREP_VIEW,
    StorePermission.DOCS_VIEW,
  ],
  [StoreUserRole.STORE_VIEWER]: [
    StorePermission.VIEW_DASHBOARD,
    StorePermission.VIEW_INVENTORY,
    StorePermission.VIEW_SALES,
    StorePermission.VIEW_CLIENTS,
    StorePermission.VIEW_TASKS,
    StorePermission.VIEW_TEAM,
    StorePermission.CASH_VIEW,
    StorePermission.PREP_VIEW,
    StorePermission.DOCS_VIEW,
    StorePermission.CONTRACTS_VIEW,
    StorePermission.REPORTS_VIEW,
    StorePermission.FINANCIAL_VIEW,
    StorePermission.VIEW_GOALS,
  ]
};
